import { getRecaptchaSiteKey } from '../config';

/**
 * Hook pro Google reCAPTCHA v3
 */

// TypeScript definice pro grecaptcha (Standard v3)
declare global {
  interface Window {
    grecaptcha?: {
      ready: (callback: () => void) => void;
      execute: (siteKey: string, options: { action: string }) => Promise<string>;
    };
    __recaptchaScriptLoading?: boolean;
  }
}

/**
 * Načte reCAPTCHA skript dynamicky
 */
async function loadRecaptchaScript(siteKey: string): Promise<void> {
  // Pokud je skript už načten, nic neděláme
  if (window.grecaptcha?.execute) {
    return;
  }

  // Pokud už probíhá načítání, počkáme
  if (window.__recaptchaScriptLoading) {
    await new Promise((resolve) => {
      const checkInterval = setInterval(() => {
        if (window.grecaptcha?.execute) {
          clearInterval(checkInterval);
          resolve(undefined);
        }
      }, 100);
    });
    return;
  }

  window.__recaptchaScriptLoading = true;

  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    // Používáme api.js místo enterprise.js
    script.src = `https://www.google.com/recaptcha/api.js?render=${siteKey}`;
    script.async = true;
    script.defer = true;

    script.onload = () => {
      // Čekáme na inicializaci objektu grecaptcha (může chvíli trvat i po načtení skriptu)
      const checkInterval = setInterval(() => {
        if (window.grecaptcha?.execute) {
          clearInterval(checkInterval);
          window.__recaptchaScriptLoading = false;
          resolve();
        }
      }, 100);

      // Bezpečnostní timeout (10s)
      setTimeout(() => {
        clearInterval(checkInterval);
        // Pokud se ani po timeoutu nenačte, resolve proběhne, ale následná kontrola v getToken selže
        if (window.__recaptchaScriptLoading) {
          window.__recaptchaScriptLoading = false;
          resolve();
        }
      }, 10000);
    };

    script.onerror = (e) => {
      console.error('Recaptcha script loading error', e);
      window.__recaptchaScriptLoading = false;
      reject(new Error('Nepodařilo se načíst reCAPTCHA skript'));
    };

    document.head.appendChild(script);
  });
}

/**
 * Získá reCAPTCHA token pro danou akci
 * @param action Název akce (např. 'CREATE_API_KEY', 'REQUEST_OTP')
 * @returns Promise s tokenem nebo null při chybě
 */
export async function getRecaptchaToken(action: string): Promise<string | null> {
  const siteKey = getRecaptchaSiteKey();
  if (!siteKey) {
    console.error('reCAPTCHA site key není nakonfigurován');
    return null;
  }

  try {
    // Načtení reCAPTCHA skriptu, pokud ještě není načten
    await loadRecaptchaScript(siteKey);

    if (!window.grecaptcha?.execute) {
      console.error('reCAPTCHA se nepodařilo načíst');
      return null;
    }

    return await new Promise<string>((resolve, reject) => {
      window.grecaptcha!.ready(async () => {
        try {
          const token = await window.grecaptcha!.execute(siteKey, {
            action: action,
          });
          resolve(token);
        } catch (error) {
          console.error('Recaptcha execute failed:', error);
          reject(error);
        }
      });
    });
  } catch (error) {
    console.error('Chyba při získávání reCAPTCHA tokenu:', error);
    return null;
  }
}

/**
 * Hook pro použití reCAPTCHA v komponentách
 */
export function useRecaptcha() {
  return {
    getToken: getRecaptchaToken,
  };
}
